import React, { useState, FormEvent, useContext } from "react";
import { Modal, Form, Button } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useErrors } from "services/customHooks/useErrors";
import { PortInModify } from "../types/PortInModify";
import moment, { Moment } from "moment";
import { useIsMounted } from "services/customHooks/useIsMounted";
import { showInfoNotification } from "components/framework/notification/NotificationUtil";
import { getFieldErrors, handleFieldOrApiErrors } from "services/util/ApiUtil";
import CloseButton from "components/framework/modals/CloseButton";
import { nameOf } from "services/util/ObjectUtil";
import { ModifyModel } from "services/apis/types/port/ModifyModel";
import DateTimeFormInput from "components/framework/forms/DateTimeFormInput";
import { OrderApi } from "services/apis/OrderApi";
import { OrderDto } from "services/apis/types/order/OrderDto";
import DropdownFormInput from "components/framework/forms/DropdownFormInput";
import { SupplementType, SupplementTypesList } from "services/apis/types/order/SupplementType";
import { SupplementModel, DefaultSupplementModel } from "./SupplementModel";
import AccountDetails from "../AccountDetails";
import { NewSupplementModel } from "services/apis/types/order/NewSupplementModel";
import { getUtcDate, isSunday } from "services/util/DateUtil";
import classNames from "classnames";
import NpacMaintenanceAlert from "../NpacMaintenanceAlert";
import { OrderHandlerType, OrderHandlerTypesList } from "services/apis/types/port/OrderHandlerType";
import { AppContext } from "services/appContext/AppContext";
import { isNumber, isValidZipCode } from "services/util/StringUtil";
import { AddError, Errors, HasErrors } from "components/framework/errorHandling/ErrorUtil";
import {
  CustomerDetailsDto,
  DefaultCustomerDetailsDto
} from "services/apis/types/port/CustomerDetailsDto";
import { PortInAccountModel } from "../types/PortInAccountModel";
import Datetime, { TimeConstraints } from "react-datetime";
import { ServiceAddressDto } from "services/apis/types/port/ServiceAddressDto";
import { useEffect } from "react";
import Select from "react-select";
import { userZoneInfos } from "components/manage/user/UserTimeZones";
import { OrderListDto } from "services/apis/types/order/OrderListDto";
import { FieldError } from "services/apis/types/FieldError";
import { EnvironmentUtil } from "services/util/EnvironmentUtil";

const isCspireUrl = EnvironmentUtil.isCspire;

type Props = {
  orders: OrderListDto[];
  closeModal: () => void;
  onActionExecuted: () => void;
};

const timeConstraints: TimeConstraints = {
  hours: { min: 0, max: 23, step: 1 },
  minutes: { min: 0, max: 59, step: 10 }, // Set step to 10 for 10-minute intervals
  seconds: { min: 0, max: 59, step: 0 }
};

export default function BulkSupplementModal(props: Props) {
  const { appContext } = useContext(AppContext);
  const theme = appContext.theme;
  const isVpop = useState<boolean>(
    () => appContext.localStorageInfo.selectedProfile?.external ?? false
  );
  const { setErrors, getErrorHandler } = useErrors();
  const [supplementModel, setSupplementModel] = useState<SupplementModel>({
    ...DefaultSupplementModel,
    dueDate: getUtcDate(new Date()),
    orderHandler: "ServiceProvider" as OrderHandlerType,
    customerDetails: DefaultCustomerDetailsDto
  });
  const [autoActivation, setAutoActivation] = useState(false);

  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const intl = useIntl();
  const isMounted = useIsMounted();
  const [supplementOptions] = useState(() =>
    isVpop
      ? SupplementTypesList.filter((s) => s.value !== SupplementType.OrderHandler)
      : SupplementTypesList
  );
  const [orderHandlerOptions] = useState(OrderHandlerTypesList);
  const [selectedTimeZone, setSelectedTimeZone] = useState<string>(
    appContext.localStorageInfo.user?.zoneInfo || "UTC"
  );
  const handleTimeZoneChange = (selectedOption) => {
    setSelectedTimeZone(selectedOption.value);
  };
  const timezoneOptions = userZoneInfos.map((zone) => ({
    value: zone.value,
    label: zone.key
  }));
  const [addressMap, setAddressMap] = useState<Map<string, ServiceAddressDto>>(new Map());
  const [selectedTime, setSelectedTime] = useState<Moment | null>(moment());
  const handleTimeChange = (value) => {
    setSelectedTime(value);
  };

  // Fetch serviceAddresses for all orders on mount or when orders change
  useEffect(() => {
    const getServiceAddressDetails = async (orderIds: string[]) => {
      const getServiceDetails = await OrderApi.getServiceAddress(orderIds);
      const map = new Map(getServiceDetails.map((addr) => [addr.orderId, addr.serviceAddress]));
      return map;
    };

    const fetchAddresses = async () => {
      const orderIds = props.orders.map((order) => order.orderId);
      if (orderIds.length > 0) {
        const map = await getServiceAddressDetails(orderIds);
        setAddressMap(map);
      }
    };

    fetchAddresses();
  }, [props.orders]);

  const handleCancelOrder = async () => {
    setShowLoading(true);
    try {
      await Promise.all(
        props.orders.map((order) =>
          OrderApi.addSupplementCancel(order.orderId, order.tenant, {
            supplementType: SupplementType.Cancel
          })
        )
      );
      showInfoNotification(
        intl.formatMessage({ id: "orders.supplement.successNotificationMessage" })
      );
      setShowConfirmationModal(false);
      props.closeModal();
    } catch (error) {
      handleFieldOrApiErrors(error);
      if (
        error &&
        typeof error === "object" &&
        "fieldErrors" in error &&
        error.fieldErrors // additionally check fieldErrors exists
      ) {
        const errorsResult = getFieldErrors(
          (error as { fieldErrors: { [key: string]: FieldError[] } }).fieldErrors
        );
        setErrors(errorsResult);
      }
    } finally {
      if (isMounted.current) {
        setShowLoadingIndicator(false);
      }
      props.onActionExecuted();
    }
  };

  const handleDueDateOrder = async () => {
    setShowLoading(true);
    try {
      await Promise.all(
        props.orders.map((order) =>
          autoActivation
            ? OrderApi.addSupplementDueDate(order.orderId, order.tenant, {
                supplementType: SupplementType.DueDate,
                dueDate: supplementModel.dueDate,
                autoActivationDate: autoActivationDateArrange(
                  supplementModel.dueDate,
                  selectedTime
                ),
                timeZone: selectedTimeZone
              })
            : OrderApi.addSupplementDueDate(order.orderId, order.tenant, {
                supplementType: SupplementType.DueDate,
                dueDate: supplementModel.dueDate
              })
        )
      );
      showInfoNotification(
        intl.formatMessage({ id: "orders.supplement.successNotificationMessage" })
      );
      setShowConfirmationModal(false);
      props.closeModal();
    } catch (error) {
      handleFieldOrApiErrors(error);
      if (
        error &&
        typeof error === "object" &&
        "fieldErrors" in error &&
        error.fieldErrors // additionally check fieldErrors exists
      ) {
        const errorsResult = getFieldErrors(
          (error as { fieldErrors: { [key: string]: FieldError[] } }).fieldErrors
        );
        setErrors(errorsResult);
      }
    } finally {
      if (isMounted.current) {
        setShowLoadingIndicator(false);
      }
      props.onActionExecuted();
    }
  };

  const handleOtherDetails = async () => {
    setShowLoadingIndicator(true);

    try {
      await Promise.all(
        props.orders.map((order) => {
          const serviceAddress = addressMap.get(order.orderId);

          const payload = {
            supplementType: SupplementType.Other,
            customerDetails: {
              ...supplementModel.customerDetails,
              serviceAddress: serviceAddress ?? supplementModel.customerDetails.serviceAddress
            }
          };

          return OrderApi.addSupplementOthers(order.orderId, order.tenant, payload);
        })
      );

      showInfoNotification(
        intl.formatMessage({ id: "orders.supplement.successNotificationMessage" })
      );
      setShowConfirmationModal(false);
      props.closeModal();
      props.onActionExecuted && props.onActionExecuted();
    } catch (error) {
      handleFieldOrApiErrors(error);
      if (error && typeof error === "object" && "fieldErrors" in error && error.fieldErrors) {
        const errorsResult = getFieldErrors(
          (error as { fieldErrors: { [key: string]: FieldError[] } }).fieldErrors
        );
        // assume setErrors is defined or add useErrors hook to set them
        setErrors(errorsResult);
      }
    } finally {
      if (isMounted.current) {
        setShowLoadingIndicator(false);
      }
      props.onActionExecuted();
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const errors = validateAccountDetails(supplementModel);
    if (supplementModel.supplementType === SupplementType.Cancel) {
      setShowConfirmationModal(true);
      return;
    }
    if (HasErrors(errors)) {
      setErrors(errors);
    } else {
      setShowLoadingIndicator(true);
      if (supplementModel.supplementType === SupplementType.DueDate) {
        handleDueDateOrder();
      }
      if (supplementModel.supplementType === SupplementType.Other) {
        handleOtherDetails();
      }
    }
  };

  return (
    <>
      <Modal
        size={supplementModel.supplementType === SupplementType.Other ? "lg" : "sm"}
        className={classNames("modal-dialog-centered", {
          "pc-modal-xlg": supplementModel.supplementType === SupplementType.Other
        })}
        isOpen={true}>
        <div className="modal-header">
          <h5 className="modal-title">
            <FormattedMessage id="orders.supplement.title" />
          </h5>
          <CloseButton close={props.closeModal} />
        </div>
        <Form onSubmit={(e) => handleSubmit(e)}>
          <div className="modal-body">
            <div className="d-flex">
              <DropdownFormInput
                className="flex-fill"
                required
                labelTranslationId="orders.supplement.supplementType"
                value={supplementModel.supplementType as string}
                options={supplementOptions}
                handleInputChange={(value: string) =>
                  setSupplementModel({
                    ...supplementModel,
                    supplementType: value as SupplementType
                  })
                }
                errorHandler={getErrorHandler(nameOf<SupplementModel>("supplementType"))}
              />
            </div>
            {supplementModel.supplementType === SupplementType.DueDate && (
              <div>
                <div className="d-flex">
                  <DateTimeFormInput
                    className="flex-fill"
                    labelTranslationId="orders.supplement.dueDate"
                    value={moment(supplementModel.dueDate)}
                    handleInputChange={(value: Moment) =>
                      setSupplementModel({
                        ...supplementModel,
                        dueDate: moment(value)
                      })
                    }
                    errorHandler={getErrorHandler(
                      nameOf<PortInModify, ModifyModel>("portInModifyModel", "dueDate")
                    )}
                  />
                </div>
                {autoActivation && (
                  <>
                    <div className="d-flex flex-column">
                      <h4 className="add-supplement-modal-section mr-3">
                        <FormattedMessage id="orders.portIn.actions.addSupplement.autoActivationTime"></FormattedMessage>
                      </h4>
                      <Datetime
                        onChange={handleTimeChange}
                        value={selectedTime || undefined}
                        input={true}
                        dateFormat={false}
                        timeConstraints={timeConstraints}
                      />
                    </div>
                    <div className="mt-3">
                      <h4 className="add-supplement-modal-section mr-3">
                        <FormattedMessage id="orders.portIn.actions.addSupplement.timeZone"></FormattedMessage>
                      </h4>
                      <Select
                        id="timeZone"
                        name="timeZone"
                        value={{ value: selectedTimeZone, label: selectedTimeZone }}
                        onChange={handleTimeZoneChange}
                        options={timezoneOptions}
                        className="time-zone-select"
                        styles={{
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            padding: 0
                          })
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
            {supplementModel.supplementType === SupplementType.OrderHandler && (
              <div className="d-flex">
                <DropdownFormInput
                  className="flex-fill"
                  required
                  labelTranslationId="orders.supplement.orderHandler"
                  value={supplementModel.orderHandler as string}
                  options={orderHandlerOptions}
                  handleInputChange={(value: string) =>
                    setSupplementModel({
                      ...supplementModel,
                      orderHandler: value as OrderHandlerType
                    })
                  }
                />
              </div>
            )}
            {supplementModel.supplementType === SupplementType.Other && (
              <>
                <div className="d-flex">
                  <div className="add-supplement-modal-section mr-3">
                    <AccountDetails
                      customerDetails={supplementModel.customerDetails}
                      setCustomerDetails={(customerDetails) =>
                        setSupplementModel({ ...supplementModel, customerDetails: customerDetails })
                      }
                      getErrorHandler={getErrorHandler}
                    />
                  </div>
                </div>
              </>
            )}
            {supplementModel.supplementType === SupplementType.DueDate &&
              isSunday(supplementModel.dueDate) && (
                <div className="d-flex">
                  <NpacMaintenanceAlert classNames="pc-npac-maintenance-warning" />
                </div>
              )}
          </div>
          <div className="modal-footer">
            <Button
              color="link"
              type="button"
              className={`${theme === "light" ? "lblue-color" : ""}`}
              onClick={props.closeModal}>
              <FormattedMessage id="orders.modify.edit.cancelButton" />
            </Button>
            <Button
              color="primary"
              type="submit"
              className={`ml-auto ${theme === "light" ? "bg-lblue no-border" : ""}`}
              disabled={showLoadingIndicator}>
              {showLoadingIndicator && <i className="fas fa-spinner fa-spin mr-2" />}
              <FormattedMessage id="orders.modify.edit.submitButton" />
            </Button>
          </div>
        </Form>
      </Modal>
      {showConfirmationModal && (
        <Modal
          className="modal-dialog-centered modal-md"
          isOpen={showConfirmationModal}
          centered
          backdrop="static">
          <div className="modal-header pb-0">
            <h6 className="modal-title">Confirm Action</h6>
            <CloseButton close={() => setShowConfirmationModal(false)} />
          </div>
          <div className="modal-body pb-0">
            <p>Are you sure you want to cancel this order?</p>
          </div>
          <div className="modal-footer">
            <Button
              color="link"
              type="button"
              className={`${theme === "light" ? "lblue-color" : ""}`}
              onClick={() => setShowConfirmationModal(false)}>
              <FormattedMessage id="confirmationModal.cancelButton" />
            </Button>
            <Button
              color="danger"
              type="button"
              className={`ml-auto ${theme === "light" ? "bg-lblue no-border" : ""}`}
              onClick={handleCancelOrder}>
              {showLoading && <i className="fas fa-spinner fa-spin mr-2" />}
              <FormattedMessage id="orders.orderDetails.portingNumber.table.confirmAction" />
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}

const autoActivationDate = (time, record: NewSupplementModel): string => {
  const formattedDate = moment.utc(record.dueDate).format("YYYY-MM-DD");
  const formattedTime = moment(time, "hh:mm A").format("HH:mm:ss");
  const combinedDateTime = `${formattedDate}T${formattedTime}`;
  return combinedDateTime;
};

const autoActivationDateArrange = (dueDate, time): string => {
  const formattedDate = moment.utc(dueDate).format("YYYY-MM-DD");
  const formattedTime = moment(time, "hh:mm A").format("HH:mm:ss");
  const combinedDateTime = `${formattedDate}T${formattedTime}`;
  return combinedDateTime;
};

const validateAccountDetails = (supplementModel: SupplementModel): Errors => {
  const errors: Errors = {};
  if (supplementModel.supplementType === SupplementType.DueDate && !supplementModel.dueDate) {
    AddError(errors, nameOf<SupplementModel>("dueDate"), "orders.order.csr.modifyDueDate.required");
  }

  if (
    supplementModel.supplementType === SupplementType.Other &&
    !supplementModel.customerDetails.accountName
  ) {
    AddError(
      errors,
      nameOf<PortInAccountModel, CustomerDetailsDto>("customerDetails", "accountName"),
      "orders.accountDetails.accountName"
    );
  }
  if (
    isCspireUrl && supplementModel.supplementType === SupplementType.Other &&
    !supplementModel.customerDetails.accountNumber
  ) {
    AddError(
      errors,
      nameOf<PortInAccountModel, CustomerDetailsDto>("customerDetails", "accountNumber"),
      "orders.accountDetails.accountNumber"
    );
  }
  if (
    supplementModel.supplementType === SupplementType.Other &&
    !supplementModel.customerDetails.authorizedName
  ) {
    AddError(
      errors,
      nameOf<PortInAccountModel, CustomerDetailsDto>("customerDetails", "authorizedName"),
      "orders.accountDetails.authorizedName"
    );
  }

  if (
    supplementModel.customerDetails.serviceAddress.zipCode &&
    !isValidZipCode(supplementModel.customerDetails.serviceAddress.zipCode)
  ) {
    AddError(
      errors,
      nameOf<PortInAccountModel, CustomerDetailsDto, ServiceAddressDto>(
        "customerDetails",
        "serviceAddress",
        "zipCode"
      ),
      "orders.portIn.stepTwo.dueDate.zipCodeError"
    );
  }

  if (
    supplementModel.supplementType === SupplementType.Other &&
    (!isNumber(supplementModel.customerDetails.billingPhoneNumber) ||
      supplementModel.customerDetails.billingPhoneNumber.length < 10 ||
      supplementModel.customerDetails.billingPhoneNumber.length > 15)
  ) {
    AddError(
      errors,
      nameOf<PortInAccountModel, CustomerDetailsDto>("customerDetails", "billingPhoneNumber"),
      "orders.portIn.stepTwo.billingPhoneNumber"
    );
  }

  return errors;
};
